import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchTestTypeAnalysis, fetchAllProjectWiseHours } from "../../../API_Service/Dashboard_Slice";
import { companyID, Dropdown } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";

const CHART_DISPLAY_LIMIT = 10;

const getBarHeightPercent = (length) => {
  if (length === 1) return "40%";
  if (length === 2) return "50%";
  return "65%";
};

const buildChartOptions = (categories, calculatedHeight, barHeightPercent) => ({
  chart: {
    type: "bar",
    stacked: true,
    toolbar: {
      show: true,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
      },
    },
    height: calculatedHeight,
    animations: {
      enabled: true,
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 150,
      },
    },
    background: "transparent",
  },
  plotOptions: {
    bar: {
      horizontal: true,
      distributed: false,
      dataLabels: {
        position: "top",
      },
      barHeight: barHeightPercent,
      borderRadius: 6,
      borderRadiusApplication: "end",
    },
  },
  dataLabels: {
    enabled: true,
    formatter: (value) => {
      return value ? value.toFixed(2) : "0.00";
    },
    style: {
      fontSize: "11px",
      fontWeight: 700,
      colors: ["#ffffff", "#000000"],
    },
    background: {
      enabled: false,
    },
    offsetX: -5,
    textAnchor: "end",
  },
  stroke: {
    show: true,
    width: 0,
    colors: ["transparent"],
  },
  xaxis: {
    categories,
    type: "category",
    labels: {
      style: {
        fontSize: "12px",
        fontWeight: 600,
        colors: "#475569",
      },
      offsetY: 5,
    },
    axisBorder: {
      show: true,
      color: "#e2e8f0",
      height: 1,
    },
    axisTicks: {
      show: false,
    },
    title: {
      text: "Hours",
      style: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#1e293b",
      },
      offsetY: 10,
    },
  },
  yaxis: {
    type: "numeric",
    axisBorder: {
      show: true,
      color: "#e2e8f0",
      width: 1,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: {
        fontSize: "12px",
        fontWeight: 600,
        colors: "#475569",
      },
      offsetX: -5,
    },
    title: {
      text: "Test Types",
      style: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#1e293b",
      },
    },
  },
  fill: {
    opacity: 1,
    type: "solid",
  },
  grid: {
    borderColor: "#e2e8f0",
    strokeDashArray: 0,
    xaxis: {
      lines: {
        show: true,
        opacity: 0.1,
      },
    },
    yaxis: {
      lines: {
        show: false,
      },
    },
    padding: {
      left: 0,
      right: 10,
      top: 0,
      bottom: 0,
    },
  },
  states: {
    hover: {
      filter: {
        type: "darken",
        value: 0.15,
      },
    },
    active: {
      filter: {
        type: "darken",
        value: 0.15,
      },
    },
  },
  colors: ["#3B82F6", "#93C5FD"],
  tooltip: {
    enabled: true,
    theme: "light",
    style: {
      fontSize: "12px",
    },
    onDatasetHover: {
      highlightDataSeries: true,
    },
    shared: true,
    intersect: false,
    fillSeriesColor: false,
    y: {
      formatter: (value) => {
        return value ? value.toFixed(2) + " hrs" : "0 hrs";
      },
      title: {
        formatter: (seriesName) => seriesName + ": ",
      },
    },
    marker: {
      show: true,
    },
    style: {
      padding: "10px 14px",
      borderRadius: "8px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    },
  },
  responsive: [
    {
      breakpoint: 1024,
      options: {
        plotOptions: {
          bar: {
            barHeight: "60%",
          },
        },
      },
    },
  ],
});

const TestTypeAnalysis = ({ selectedProjectID = 0, selectedProjectName = "", minHeight = 400, onClearFilter, onProjectSelected, projectListProp = [] }) => {
  const dispatch = useDispatch();
  const [series, setSeries] = useState([]);
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [localSelectedProjectID, setLocalSelectedProjectID] = useState(0);
  const [displayProjectName, setDisplayProjectName] = useState("");
  const [projectList, setProjectList] = useState([]);
  const [chartHeight, setChartHeight] = useState(400);
  const [testTypeNames, setTestTypeNames] = useState([]);
  const [fullTestTypeData, setFullTestTypeData] = useState([]);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allCompanyProjects, setAllCompanyProjects] = useState([]);

  const loadTestTypeAnalysis = useCallback((projectID = 0) => {
    if (!companyID) {
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }

    console.log("📊 Loading Test Type Analysis - CompanyID:", companyID, "ProjectID:", projectID);
    setIsLoading(true);
    setError(null);
    setIsEmpty(false);

    // Clear existing data when loading new project
    setSeries([]);
    setOptions(null);

    dispatch(fetchTestTypeAnalysis({ companyID, projectID: projectID }))
      .then((result) => {
        console.log("🔍 Test Type Analysis Result:", result);
        console.log("🔍 Result Payload Type:", typeof result.payload, "Is Array:", Array.isArray(result.payload));

        if (!result || !result.payload) {
          console.error("❌ No payload in result");
          setError("Invalid response from server");
          setIsLoading(false);
          return;
        }

        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
          console.log("✅ Extracted data from payload.data");
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
          console.log("✅ Using payload directly as array");
        } else {
          console.error("❌ Payload is not an array:", result.payload);
          setError("Invalid data format from server");
          setIsLoading(false);
          return;
        }

        // Filter out entries with 0 total hours
        const originalLength = data.length;
        data = data.filter((d) => d.TotalHours > 0);
        console.log(`📊 Filtered data: ${originalLength} -> ${data.length} (removed ${originalLength - data.length} zero-hour entries)`);

        if (!Array.isArray(data) || data.length === 0) {
          console.warn("⚠️ No test type data available after filtering");
          setIsEmpty(true);
          // Clear the chart when no data
          setSeries([]);
          setOptions(null);
          setIsLoading(false);
          return;
        }

        // Extract project list if this is all-projects view (projectID = 0)
        if (projectID === 0 && data.length > 0) {
          const uniqueProjects = [];
          const seenProjects = new Set();

          data.forEach((item) => {
            const projID = item.projectId || item.ProjectId || 0;
            const projName = item.projectName || item.ProjectName || "Unknown";

            if (projID && !seenProjects.has(projID)) {
              seenProjects.add(projID);
              uniqueProjects.push({ id: projID, name: projName });
            }
          });

          setProjectList(uniqueProjects);
          console.log("📋 Available Projects:", uniqueProjects);
        }

        // Keep the full dataset for the "View All" modal
        setFullTestTypeData(data);

        // Only show the top N entries in the main chart
        const displayData = data.slice(0, CHART_DISPLAY_LIMIT);

        const names = displayData.map((t) => t.TestTypeName || "Unknown");
        const billableHours = displayData.map((t) => parseFloat(t.BillableHours) || 0);
        const nonBillableHours = displayData.map((t) => parseFloat(t.NonBillableHours) || 0);

        console.log("📋 Test Types:", names);
        console.log("💰 Billable Hours:", billableHours);
        console.log("⏱️ Non-Billable Hours:", nonBillableHours);

        setTestTypeNames(names);
        setSeries([
          {
            name: "Billable Hours",
            data: billableHours,
          },
          {
            name: "Non-Billable Hours",
            data: nonBillableHours,
          },
        ]);

        // Smart chart height calculation - Dynamic sizing (capped at 10 items now)
        let calculatedHeight;
        if (names.length === 1) {
          calculatedHeight = 200; // Single item - compact
        } else if (names.length === 2) {
          calculatedHeight = 250; // Two items
        } else {
          calculatedHeight = 35 * names.length; // Standard scaling for up to 10
        }

        // Ensure it matches or exceeds the minimum height (for side-by-side layout consistency)
        calculatedHeight = Math.max(calculatedHeight, minHeight);

        const barHeightPercent = getBarHeightPercent(names.length);

        setChartHeight(calculatedHeight);
        console.log("📏 Chart Height:", calculatedHeight, "Bar Height:", barHeightPercent, "Test Types:", names.length);

        setOptions(buildChartOptions(names, calculatedHeight, barHeightPercent));

        setError(null);
      })
      .catch((err) => {
        console.error("❌ TestTypeAnalysis Error:", err);
        console.error("Error Message:", err?.message);
        console.error("Error Status:", err?.status);

        // Clear UI on error
        setSeries([]);
        setOptions(null);
        setProjectList([]);

        setError(err?.message || "Failed to load test type analysis");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [companyID, dispatch]);

  // Sync parent selectedProjectID with local state and find project name from projectList
  useEffect(() => {
    console.log("📊 Parent selectedProjectID changed to:", selectedProjectID);
    setLocalSelectedProjectID(selectedProjectID);

    if (selectedProjectID === 0) {
      setDisplayProjectName("");
      console.log("✅ Cleared project name (ID=0)");
    } else if (projectList && projectList.length > 0) {
      const project = projectList.find((p) => p.id === selectedProjectID);
      if (project) {
        setDisplayProjectName(project.name);
        console.log("✅ Found project name from list:", project.name);
      } else {
        console.warn("⚠️ Project ID", selectedProjectID, "not found in projectList of", projectList.length, "items");
      }
    } else {
      console.warn("⚠️ ProjectList is empty, cannot find project name");
    }
  }, [selectedProjectID, projectList]);

  // Sync minHeight changes - ensure chart height matches partner chart
  useEffect(() => {
    if (minHeight && chartHeight < minHeight) {
      console.log("📏 Syncing height to match partner chart. Current:", chartHeight, "Min:", minHeight);
      setChartHeight(minHeight);
    }
  }, [minHeight, chartHeight]);

  // Load test type analysis when local selection changes
  useEffect(() => {
    loadTestTypeAnalysis(localSelectedProjectID);
  }, [localSelectedProjectID, loadTestTypeAnalysis]);

  // Fetch the complete project list (not limited to top 10) for the dropdown
  useEffect(() => {
    if (!companyID) return;

    dispatch(fetchAllProjectWiseHours({ companyID }))
      .then((result) => {
        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        } else {
          data = [];
        }

        const mapped = data
          .map((p) => ({
            id: p.projectId || p.ProjectId || 0,
            name: p.projectName || p.ProjectName || "Unknown",
          }))
          .filter((p) => p.id);

        setAllCompanyProjects(mapped);
      })
      .catch((err) => {
        console.error("Failed to load full project list:", err);
      });
  }, [dispatch]);

  useDashboardSignalR(() => loadTestTypeAnalysis(localSelectedProjectID));

  // Build full (unlimited) chart series/options for the "View All" modal
  const allTestTypeNames = useMemo(
    () => fullTestTypeData.map((t) => t.TestTypeName || "Unknown"),
    [fullTestTypeData],
  );

  const allSeries = useMemo(
    () => [
      {
        name: "Billable Hours",
        data: fullTestTypeData.map((t) => parseFloat(t.BillableHours) || 0),
      },
      {
        name: "Non-Billable Hours",
        data: fullTestTypeData.map((t) => parseFloat(t.NonBillableHours) || 0),
      },
    ],
    [fullTestTypeData],
  );

  const allChartHeight = useMemo(
    () => Math.max(300, 35 * allTestTypeNames.length),
    [allTestTypeNames],
  );

  const allOptions = useMemo(() => {
    if (allTestTypeNames.length === 0) return null;
    return buildChartOptions(allTestTypeNames, allChartHeight, getBarHeightPercent(allTestTypeNames.length));
  }, [allTestTypeNames, allChartHeight]);

  // Prefer the full company project list (unlimited) over the internally
  // discovered list or the top-10 list handed down from the Project-wise
  // Hours chart, so every project shows up here.
  const projectSourceList =
    allCompanyProjects && allCompanyProjects.length > 0
      ? allCompanyProjects
      : projectList && projectList.length > 0
        ? projectList
        : projectListProp;

  const projectDropdownOptions = useMemo(
    () => [
      { label: "All Projects", value: 0 },
      ...(projectSourceList || []).map((p) => ({ label: p.name || "Unknown", value: p.id })),
    ],
    [projectSourceList],
  );

  const handleProjectDropdownChange = (projectId) => {
    const numericId = Number(projectId);
    const project = (projectSourceList || []).find((p) => p.id === numericId);
    setLocalSelectedProjectID(numericId);
    if (project) {
      setDisplayProjectName(project.name);
    }
    if (onProjectSelected && typeof onProjectSelected === "function") {
      onProjectSelected(numericId, project?.name || "");
    }
  };

  if (error) {
    return (
      <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Error loading test type analysis: {error}</p>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-6">
          Test Type Analysis
        </h3>
        <div className="text-center text-slate-500 py-12">
          <span className="material-symbols-outlined text-4xl mb-2 block opacity-30">inbox</span>
          <p className="font-medium">No test type data available</p>
        </div>
      </div>
    );
  }

  if (isLoading || !options) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">
            Test Type Analysis
          </h3>
        </div>
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
            Test Type Analysis

          </h3>
          <p className="text-sm text-slate-500 mt-1">Track test type distribution and hours</p>
        </div>
        <div className="flex items-center gap-3">
          {fullTestTypeData.length > CHART_DISPLAY_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAllModal(true)}
              className="px-4 py-2 text-sm font-semibold text-[#08757C] border-2 border-[#08757C]/30 rounded-lg hover:bg-[#08757C]/5 transition-colors"
            >
              View All 
              {/* ({fullTestTypeData.length}) */}
            </button>
          )}
          {/* Project Selector Dropdown - Right Corner */}
          <div className="w-56">
            <Dropdown
              options={projectDropdownOptions}
              value={localSelectedProjectID}
              onChange={handleProjectDropdownChange}
              placeholder="All Projects"
            />
          </div>

          {selectedProjectID > 0 && onClearFilter && (
            <button
              onClick={onClearFilter}
              className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
            >
              <span>✕</span>
              <span>Clear</span>
            </button>
          )}
          <div className="p-3 bg-blue-100 rounded-full">
            <span className="material-symbols-outlined text-blue-600">analytics</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#3B82F6" }}></div>
          <span className="text-sm font-semibold text-slate-700">Billable Hours</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#93C5FD" }}></div>
          <span className="text-sm font-semibold text-slate-700">Non-Billable Hours</span>
        </div>
      </div>

      {/* Chart with Vertical Scrolling */}
      {series.length > 0 && options ? (
        <div className="rounded-xl overflow-hidden bg-white p-4">
          {/* Scrollable Container - Shows all items with vertical scroll */}
          <div
            className="overflow-y-auto overflow-x-hidden"
            style={{
              maxHeight: testTypeNames.length > 10 ? "600px" : "auto",
              scrollBehavior: "smooth",
              width: "100%"
            }}
          >
            <Chart
              options={options}
              series={series}
              type="bar"
              height={testTypeNames.length > 10 ? testTypeNames.length * 35 : Math.max(chartHeight, 400)}
              width="100%"
            />
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          No data available
        </div>
      )}

      {/* Project Selector List - Only show when viewing all projects (localSelectedProjectID = 0) */}
      {localSelectedProjectID === 0 && projectList && projectList.length > 0 && (
        <div className="mt-8 pt-8 border-t border-slate-200">
          <p className="text-sm font-semibold text-slate-700 mb-4">📌 Click on a project to filter this analysis:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-40 overflow-y-auto">
            {projectList.map((project, idx) => (
              <button
                key={idx}
                onClick={() => {
                  console.log("✅ Selected Project from TestTypeAnalysis:", project.name, "ID:", project.id);
                  setLocalSelectedProjectID(project.id);
                  setDisplayProjectName(project.name);
                  if (onProjectSelected && typeof onProjectSelected === "function") {
                    onProjectSelected(project.id, project.name);
                  }
                }}
                className="p-3 rounded-lg font-semibold text-sm transition-all bg-slate-100 text-slate-700 hover:bg-blue-500 hover:text-white border-2 border-transparent hover:border-blue-600"
              >
                <span className="block truncate text-left">{project.name}</span>
                <span className="text-xs opacity-75 block text-left">ID: {project.id}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>

    {/* View All Test Types Modal */}
    {showAllModal && (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowAllModal(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h3 className="text-xl font-bold text-slate-900">All Test Types</h3>
              <p className="text-sm text-slate-500 mt-1">Complete list of test types and hours</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAllModal(false)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-slate-600">close</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {allSeries.length > 0 && allOptions ? (
              <>
                {/* Legend */}
                <div className="flex items-center gap-6 mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#3B82F6" }}></div>
                    <span className="text-sm font-semibold text-slate-700">Billable Hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#93C5FD" }}></div>
                    <span className="text-sm font-semibold text-slate-700">Non-Billable Hours</span>
                  </div>
                  <div className="ml-auto text-sm text-slate-500">
                    Total Test Types: <span className="font-bold text-slate-900">{fullTestTypeData.length}</span>
                  </div>
                </div>

                <Chart
                  options={allOptions}
                  series={allSeries}
                  type="bar"
                  height={allChartHeight}
                />
              </>
            ) : (
              <div className="text-center text-slate-500 py-12">
                <span className="material-symbols-outlined text-4xl mb-2 block opacity-30">inbox</span>
                <p className="font-medium">No data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default TestTypeAnalysis;
